/**
 * Smart charging (Phase G).
 *
 * Surfaces the sugar endpoints first (`Max kW` and `Site cap`) for the
 * primary UX, then a list of persisted profiles (`GET /smart/:chargerId`)
 * and an "effective schedule preview"
 * (`GET /smart/:chargerId/composite-schedule?duration_seconds=3600`).
 */

import React, { useCallback, useState } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';

import OcppActionButton from '../components/OcppActionButton';
import OcppEmptyState from '../components/OcppEmptyState';
import OcppErrorBanner from '../components/OcppErrorBanner';
import OcppKvList from '../components/OcppKvList';
import OcppLoadingState from '../components/OcppLoadingState';
import OcppSectionCard from '../components/OcppSectionCard';
import OcppStatusPill from '../components/OcppStatusPill';
import { OcppTheme } from '../components/OcppTheme';
import {
  clearChargingProfile,
  getCompositeSchedule,
  listChargingProfiles,
  setMaxKw,
  setSiteCap,
} from '../services';
import { isOcppSuccess, mapOcppApiError, type OcppApiErrorInfo } from '../utils/apiErrors';
import { formatOcppTimestamp } from '../utils/statusFormat';
import type {
  ChargingProfile,
  CompositeScheduleResponse,
  ListChargingProfilesResponse,
  MaxKwBody,
  SiteCapBody,
} from '../models';
import type { OcppTabProps } from './types';

const SmartChargingTab: React.FC<OcppTabProps> = ({ chargerId }) => {
  const [profiles, setProfiles] = useState<ChargingProfile[]>([]);
  const [composite, setComposite] = useState<CompositeScheduleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<OcppApiErrorInfo | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [maxKwValue, setMaxKwValue] = useState<string>('7');
  const [siteCapValue, setSiteCapValue] = useState<string>('30');

  const load = useCallback(async () => {
    setLoading(true);
    const [profileRes, scheduleRes] = await Promise.all([
      listChargingProfiles(chargerId),
      getCompositeSchedule(chargerId, { duration_seconds: 3600, charging_rate_unit: 'W' }),
    ]);
    if (isOcppSuccess(profileRes.status)) {
      const payload = profileRes.payload as ListChargingProfilesResponse | null;
      setProfiles(payload?.profiles ?? []);
      setError(null);
    } else {
      setError(mapOcppApiError(profileRes.status, profileRes.payload));
    }
    if (isOcppSuccess(scheduleRes.status)) {
      setComposite(scheduleRes.payload as CompositeScheduleResponse | null);
    }
    setLoading(false);
  }, [chargerId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const onSetMaxKw = useCallback(async () => {
    const kw = parseFloat(maxKwValue);
    if (Number.isNaN(kw) || kw <= 0) {
      setError({
        title: 'Invalid kW',
        message: 'Enter a positive number.',
        blockedByGate: false,
        featureGated: false,
        status: 400,
      });
      return;
    }
    setBusy('maxKw');
    const res = await setMaxKw(chargerId, { kw } as MaxKwBody);
    if (isOcppSuccess(res.status)) {
      setError(null);
      await load();
    } else {
      setError(mapOcppApiError(res.status, res.payload));
    }
    setBusy(null);
  }, [chargerId, maxKwValue, load]);

  const onSetSiteCap = useCallback(async () => {
    const kw = parseFloat(siteCapValue);
    if (Number.isNaN(kw) || kw <= 0) {
      setError({
        title: 'Invalid kW',
        message: 'Enter a positive number.',
        blockedByGate: false,
        featureGated: false,
        status: 400,
      });
      return;
    }
    setBusy('siteCap');
    const res = await setSiteCap(chargerId, { kw } as SiteCapBody);
    if (isOcppSuccess(res.status)) {
      setError(null);
      await load();
    } else {
      setError(mapOcppApiError(res.status, res.payload));
    }
    setBusy(null);
  }, [chargerId, siteCapValue, load]);

  const onClearProfile = useCallback(
    async (profile: ChargingProfile) => {
      setBusy(`clear-${profile.profile_id}`);
      const res = await clearChargingProfile(chargerId, {
        charging_profile_id: profile.profile_id,
      });
      if (isOcppSuccess(res.status)) {
        setError(null);
        await load();
      } else {
        setError(mapOcppApiError(res.status, res.payload));
      }
      setBusy(null);
    },
    [chargerId, load]
  );

  if (loading) return <OcppLoadingState label="Loading smart charging…" />;

  return (
    <View>
      {error ? <OcppErrorBanner error={error} onRetry={load} /> : null}

      <OcppSectionCard
        title="Cap charger to"
        subtitle="Builds a TxDefaultProfile at stack level 1. Replaces the existing 'Max kW' cap."
      >
        <View style={styles.inlineRow}>
          <TextInput
            value={maxKwValue}
            onChangeText={setMaxKwValue}
            keyboardType="numeric"
            style={styles.input}
            placeholder="kW"
            placeholderTextColor={OcppTheme.textDim}
          />
          <Text style={styles.unit}>kW</Text>
          <OcppActionButton
            label="Apply"
            icon="speed"
            onPress={onSetMaxKw}
            loading={busy === 'maxKw'}
          />
        </View>
      </OcppSectionCard>

      <OcppSectionCard
        title="Site-wide cap"
        subtitle="ChargePointMaxProfile at stack 0, connector 0 — limits the entire site."
      >
        <View style={styles.inlineRow}>
          <TextInput
            value={siteCapValue}
            onChangeText={setSiteCapValue}
            keyboardType="numeric"
            style={styles.input}
            placeholder="kW"
            placeholderTextColor={OcppTheme.textDim}
          />
          <Text style={styles.unit}>kW</Text>
          <OcppActionButton
            label="Apply"
            icon="hub"
            onPress={onSetSiteCap}
            loading={busy === 'siteCap'}
            variant="secondary"
          />
        </View>
      </OcppSectionCard>

      <OcppSectionCard
        title="Effective schedule (next 60 min)"
        subtitle="What the charger believes it will follow per its current profile stack."
      >
        {composite?.schedule?.charging_schedule_period?.length ? (
          <CompositeSchedulePreview composite={composite} />
        ) : (
          <OcppEmptyState
            icon="show-chart"
            title="No composite schedule"
            description="Either the charger has no active profile or it does not support composite-schedule lookup."
          />
        )}
      </OcppSectionCard>

      <OcppSectionCard
        title="Persisted profiles"
        subtitle="Mirror of profiles the charger reported back as Accepted."
      >
        {profiles.length === 0 ? (
          <OcppEmptyState icon="rule" title="No profiles set yet" />
        ) : (
          profiles.map((profile) => (
            <ProfileRow
              key={profile.id}
              profile={profile}
              busy={busy === `clear-${profile.profile_id}`}
              onClear={() => onClearProfile(profile)}
            />
          ))
        )}
      </OcppSectionCard>
    </View>
  );
};

export default SmartChargingTab;

const CompositeSchedulePreview: React.FC<{ composite: CompositeScheduleResponse }> = ({
  composite,
}) => {
  const periods = composite.schedule?.charging_schedule_period ?? [];
  return (
    <View style={{ gap: 8 }}>
      <Text style={styles.previewMeta}>
        Starts {formatOcppTimestamp(composite.schedule.schedule_start)} · {composite.schedule.duration}s
      </Text>
      {periods.map((p, idx) => (
        <View key={`${idx}-${p.start_period_seconds}`} style={styles.periodRow}>
          <Text style={styles.periodTime}>+{p.start_period_seconds}s</Text>
          <Text style={styles.periodLimit}>
            {p.limit} {composite.schedule.charging_rate_unit}
          </Text>
        </View>
      ))}
    </View>
  );
};

const ProfileRow: React.FC<{ profile: ChargingProfile; busy: boolean; onClear: () => void }> = ({
  profile,
  busy,
  onClear,
}) => {
  return (
    <View style={styles.profileRow}>
      <View style={{ flex: 1 }}>
        <View style={styles.profileHeader}>
          <Text style={styles.profileName}>#{profile.profile_id}</Text>
          <OcppStatusPill label={profile.purpose} color={OcppTheme.accent} size="sm" />
          <OcppStatusPill label={profile.kind} color={OcppTheme.info} size="sm" />
        </View>
        <OcppKvList
          items={[
            { key: 'Stack', value: String(profile.stack_level) },
            { key: 'Connector', value: profile.connector_id == null ? '—' : String(profile.connector_id) },
            { key: 'Recurrency', value: profile.recurrency ?? '—' },
            { key: 'Valid', value: `${profile.valid_from ?? '—'} → ${profile.valid_to ?? '—'}` },
          ]}
        />
      </View>
      <OcppActionButton
        label="Clear"
        icon="delete"
        variant="danger"
        loading={busy}
        onPress={onClear}
        style={{ alignSelf: 'flex-start' }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  inlineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  input: {
    flex: 1,
    backgroundColor: OcppTheme.surfaceAlt,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: OcppTheme.border,
    paddingHorizontal: 12,
    paddingVertical: 8,
    color: OcppTheme.textPrimary,
    fontSize: 14,
  },
  unit: {
    color: OcppTheme.textMuted,
    fontSize: 13,
    fontWeight: '600',
  },
  previewMeta: {
    color: OcppTheme.textMuted,
    fontSize: 12,
  },
  periodRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  periodTime: {
    color: OcppTheme.textSecondary,
    fontSize: 13,
  },
  periodLimit: {
    color: OcppTheme.textPrimary,
    fontSize: 13,
    fontWeight: '700',
  },
  profileRow: {
    flexDirection: 'row',
    paddingVertical: 10,
    gap: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: OcppTheme.divider,
  },
  profileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  profileName: {
    color: OcppTheme.textPrimary,
    fontSize: 14,
    fontWeight: '700',
  },
});
